/**
 * Decodes the character references a page writes into its title and description (`&uuml;`, `&ndash;`, `&#39;`). Shared by the Worker that
 * reads a page and the app that shows a title it saved earlier, so a title never reaches the screen as "Berliner Beauftragte f&uuml;r Datenschutz".
 * Unknown names are left as they are.
 */
const LETTERS: Record<string, string> = {}
for (const [names, chars] of [
  ['agrave aacute acirc atilde auml aring aelig ccedil egrave eacute ecirc euml', 'àáâãäåæçèéêë'],
  ['igrave iacute icirc iuml eth ntilde ograve oacute ocirc otilde ouml oslash', 'ìíîïðñòóôõöø'],
  ['ugrave uacute ucirc uuml yacute thorn yuml', 'ùúûüýþÿ'],
  ['Agrave Aacute Acirc Atilde Auml Aring AElig Ccedil Egrave Eacute Ecirc Euml', 'ÀÁÂÃÄÅÆÇÈÉÊË'],
  ['Igrave Iacute Icirc Iuml ETH Ntilde Ograve Oacute Ocirc Otilde Ouml Oslash', 'ÌÍÎÏÐÑÒÓÔÕÖØ'],
  ['Ugrave Uacute Ucirc Uuml Yacute THORN', 'ÙÚÛÜÝÞ'],
] as const) names.split(' ').forEach((name, index) => { LETTERS[name] = chars[index]! })

const NAMED: Record<string, string> = {
  ...LETTERS,
  amp: '&', apos: "'", gt: '>', lt: '<', nbsp: ' ', quot: '"', szlig: 'ß', ndash: '–', mdash: '—', hellip: '…', lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”',
  bull: '•', middot: '·', copy: '©', reg: '®', trade: '™', euro: '€', pound: '£', laquo: '«', raquo: '»', times: '×', deg: '°',
}

export function decodeHtmlEntities(value: string): string {
  if (!value.includes('&')) return value
  return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi, (entity, key: string) => {
    const codePoint = key.startsWith('#x') || key.startsWith('#X')
      ? Number.parseInt(key.slice(2), 16)
      : key.startsWith('#') ? Number.parseInt(key.slice(1), 10) : null
    if (codePoint !== null) {
      return Number.isInteger(codePoint) && codePoint >= 0 && codePoint <= 0x10ffff ? String.fromCodePoint(codePoint) : entity
    }
    // Names are case-sensitive for letters (&Auml; is not &auml;); the few symbols are not.
    return NAMED[key] ?? NAMED[key.toLowerCase()] ?? entity
  })
}
